import {
  useQuery,
  UseQueryOptions,
  keepPreviousData,
} from '@tanstack/react-query';
import { screenAPI } from '../screenAPI';
import type {
  ScreensResult,
  ScreenFilterDef,
  ScreenQueryParams,
} from '@/entities/screen';

export function useScreensQuery(
  params: ScreenQueryParams,
  options?: UseQueryOptions<ScreensResult, unknown, ScreensResult, any[]>
) {
  const {
    filters = {},
    search = '',
    page = 0,
    rowsPerPage = 10,
  } = params || {};

  const effectiveSearch = search.length > 2 ? search : '';
  const effectivePage = effectiveSearch ? 0 : page;

  return useQuery({
    queryKey: [
      'screens',
      { filters, search: effectiveSearch, page: effectivePage, rowsPerPage },
    ],
    queryFn: () =>
      screenAPI.fetchScreens({
        filters,
        search: effectiveSearch,
        page: effectivePage,
        rowsPerPage,
      }),
    staleTime: 30_000,
    ...options,
  });
}

export function useScreenFiltersQuery(
  options?: UseQueryOptions<
    ScreenFilterDef[],
    unknown,
    ScreenFilterDef[],
    any[]
  >
) {
  return useQuery({
    queryKey: ['screenFilters'],
    queryFn: () => screenAPI.fetchScreenFilters(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

export function useScreenByIdQuery(
  id: string | null,
  day?: string,
  options?: UseQueryOptions<any, unknown, any, any[]>
) {
  return useQuery({
    queryKey: ['screen', id, day],
    queryFn: () =>
      screenAPI.getScreenById({
        id,
        day,
      }),
    enabled: !!id, // Only run query if id exists
    staleTime: 30_000,
    placeholderData: keepPreviousData, // Keep previous data while fetching new data to prevent page reload
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    ...options,
  });
}
